import type { Variable } from "../../../types";
import {
  getNodeIdForContextVariable,
  isHolderLegalGlobalType,
  isHolderValuelessGlobalType,
} from "@/workflow_api/hooks/variables/useVariableResolver";
import {
  CONTEXT_GLOBAL_TYPES,
  LEGACY_CONTEXT_VARIABLE_MAP,
  type BaseField,
} from "../types";

export type DefaultValueObject = {
  type: string;
  variable?: string;
  value?: string;
  node_id?: string;
  global_type?: string;
  field?: string;
  response_key?: string;
  key_name?: string;
  output_name?: string;
  field_id?: string;
  id?: string;
  start?: string;
  end?: string;
};

const asObjectWithType = (value: unknown): DefaultValueObject | null =>
  value && typeof value === "object" ? (value as DefaultValueObject) : null;

const deformatInteroperabilityVariable = (dv: DefaultValueObject) => ({
  id: `${dv.field}_$key$_${dv.response_key}_$keyname$_${dv.key_name || ""}`,
  name: dv.response_key || "",
  type: "string",
});

const deformatCompanyOrPersonVariable = (dv: DefaultValueObject) => {
  const globalType = dv.global_type ?? "";
  const value = (dv.value as string) || "";
  return {
    id: isHolderValuelessGlobalType(globalType) ? globalType : value,
    name: (dv.variable as string) || "",
    node_id: getNodeIdForContextVariable(globalType, value),
    global_type: globalType,
    type: "string",
  };
};

const deformatGlobalTypeVariable = (dv: DefaultValueObject) => {
  const globalType = dv.global_type ?? "";
  if (dv.node_id && globalType.toUpperCase().includes("EXECUTION")) {
    const suffix =
      globalType === "NODE_EXECUTION_START"
        ? "execution_start"
        : "execution_finish";
    return {
      id: `${dv.node_id}_${suffix}`,
      name: dv.variable || "",
      node_id: dv.node_id,
      global_type: globalType,
      type: "date",
    };
  }

  if (globalType === "IMPERSONATING") {
    return {
      id: "impersonation",
      name: dv.variable || "",
      node_id: "impersonation-variables",
      global_type: globalType,
      type: "boolean",
    };
  }

  const getNodeId = (globalType: string, value: string) => {
    if (CONTEXT_GLOBAL_TYPES.has(globalType)) {
      return getNodeIdForContextVariable(globalType, value);
    } else if (globalType === "COMPANY_FIELD") {
      return "expedient-variables";
    }
    return "global-variables";
  };

  return {
    id: dv.value || "",
    name: dv.variable || "",
    node_id: getNodeId(globalType, dv.value || ""),
    global_type: globalType,
    type: "string",
  };
};

const deformatLegacyOrNodeVariable = (dv: DefaultValueObject) => {
  const legacyKey = dv.output_name || dv.variable;
  const migration = legacyKey
    ? LEGACY_CONTEXT_VARIABLE_MAP[legacyKey]
    : undefined;
  if (migration) {
    return {
      id: migration.id,
      name: dv.variable || "",
      node_id: getNodeIdForContextVariable(migration.global_type, migration.id),
      global_type: migration.global_type,
      type: "string",
    };
  }
  return buildVariableDefaultValue(dv as Required<DefaultValueObject>);
};

export const deformatDefaultValue = (baseField: BaseField): unknown => {
  const dv = asObjectWithType(baseField.default_value);
  const linkedVar = asObjectWithType(baseField.config?.linked_variable);

  if (!dv && linkedVar?.type === "variable") {
    return deformatDefaultValue({
      ...baseField,
      default_value: baseField.config.linked_variable,
    });
  }

  if (dv?.type?.toLowerCase() === "variable") {
    // COMPANY_FIELD / LEGAL_INFO (holder-legal) resolve by value; PERSON_TYPE
    // and DOCUMENT_TYPE by the global type itself.
    const isCompanyOrPerson =
      isHolderLegalGlobalType(dv.global_type) ||
      isHolderValuelessGlobalType(dv.global_type);
    if (isCompanyOrPerson) return deformatCompanyOrPersonVariable(dv);
    if (dv.global_type) return deformatGlobalTypeVariable(dv);
    return deformatLegacyOrNodeVariable(dv);
  }

  if (linkedVar?.type === "interoperability") {
    return deformatInteroperabilityVariable(linkedVar);
  }

  if (dv?.type === "interoperability") {
    return deformatInteroperabilityVariable(dv);
  }

  return undefined;
};

const buildVariableDefaultValue = (variable: {
  id: string;
  variable: string;
  node_id: string;
  output_name: string;
  field_id: string;
}): Variable => {
  // expedient fields variable includes the field_id and the node_id at the same time
  // And the output name contains the namespace separator '.'
  if (
    variable.output_name?.includes(".") &&
    variable.field_id &&
    variable.node_id
  ) {
    return {
      id: `${variable?.node_id}-${variable?.output_name}`,
      name: variable?.variable,
      node_id: `${variable?.node_id}-expedient-${variable?.field_id}`,
      output_name: variable?.output_name,
      field_id: variable?.field_id,
      type: "string",
    };
  }

  if (variable.output_name) {
    return {
      id: `${variable?.node_id}-${variable?.output_name}`,
      name: variable?.variable,
      node_id: variable?.node_id,
      output_name: variable?.output_name,
      field_id: variable?.field_id,
      type: "string",
    };
  }
  return {
    id: variable?.node_id,
    name: variable?.variable,
    node_id: variable?.node_id,
    output_name: variable?.output_name,
    field_id: variable?.field_id,
    type: "string",
  };
};
